import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../ui/select";
import { Tooltip } from "../../../ui/tooltip";

import { 
  InfoIcon, 
  Trash2Icon, 
  PlusIcon, 
  ChevronDownIcon, 
  ChevronRightIcon 
} from "lucide-react";
import { DecisionCondition, DecisionNodeData, DecisionVariablesRegistry } from "../utils/types";

interface DecisionRuleBuilderProps {
  decisionData: DecisionNodeData;
  onChange: (decisionData: DecisionNodeData) => void;
  availableNodes: { id: string; label: string; entityType?: string; content?: string; identifier?: string }[];
  decisionVariablesRegistry: DecisionVariablesRegistry;
  onUpdateEdges?: (decisionData: DecisionNodeData) => void;
  onCreateNodeForPath?: (pathType: 'true' | 'false') => Promise<string>;
}

// Helper function to generate unique condition IDs
const generateConditionId = () => `condition_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const defaultCondition = (): DecisionCondition => ({
  id: generateConditionId(),
  variable: "",
  operator: "==",
  value: "",
  type: "value"
});

const DecisionRuleBuilder: React.FC<DecisionRuleBuilderProps> = ({
  decisionData,
  onChange,
  availableNodes,
  decisionVariablesRegistry,
  onUpdateEdges,
  onCreateNodeForPath
}) => {
  
  
  // Collapsible state for Conditions section
  const [conditionsOpen, setConditionsOpen] = useState(true);

  // Helper function to get variable type from registry
  const getVariableType = (variableName: string): "boolean" | "value" => {
    const variable = decisionVariablesRegistry[variableName];
    return variable?.type === "boolean" ? "boolean" : "value";
  };

  // Helper function to get default operator for variable type
  const getDefaultOperator = (type: "boolean" | "value"): "==" | "!=" | "contains" | ">" | "<" | ">=" | "<=" | "is_true" | "is_false" | "equals" | "not_equals" | "greater_than" | "less_than" | "greater_than_or_equal" | "less_than_or_equal" => {
    return type === "boolean" ? "is_true" : "==";
  };

  // Handler for changing a specific condition (including joiner)
  const handleConditionChange = (idx: number, updates: Partial<DecisionCondition>) => {
    let finalUpdates = { ...updates };
    
    // If variable is being changed, automatically determine type and operator
    if (updates.variable !== undefined) {
      const variableType = getVariableType(updates.variable);
      finalUpdates = {
        ...finalUpdates,
        type: variableType,
        operator: getDefaultOperator(variableType),
        // Clear value if switching to boolean type
        value: variableType === "boolean" ? "" : updates.value || ""
      };
    }
    
    const newConditions = decisionData.conditions.map((cond, i) =>
      i === idx ? { ...cond, ...finalUpdates } : cond
    );
    onChange({ ...decisionData, conditions: newConditions });
  };

  // Handler for changing the joiner
  const handleJoinerChange = (idx: number, joiner: "and" | "or") => {
    const newConditions = decisionData.conditions.map((cond, i) =>
      i === idx ? { ...cond, joiner } : cond
    );
    onChange({ ...decisionData, conditions: newConditions });
  };

  // Handler for adding a new condition
  const handleAddCondition = () => {
    onChange({
      ...decisionData,
      conditions: [
        ...decisionData.conditions,
        { ...defaultCondition(), joiner: "and" }
      ]
    });
  };

  // Handler for removing a condition
  const handleRemoveCondition = (idx: number) => {
    const newConditions = decisionData.conditions.filter((_, i) => i !== idx);
    onChange({ ...decisionData, conditions: newConditions });
  };

  const handleTruePathChange = (nodeId: string) => {
    // console.log(`[DecisionRuleBuilder] True path changing from "${decisionData.truePath}" to "${nodeId}"`);
    const updatedData = {
      ...decisionData,
      truePath: nodeId || null
    };
    onChange(updatedData);
    // console.log("[DecisionRuleBuilder] Calling onUpdateEdges for true path change");
    onUpdateEdges?.(updatedData);
  };

  const handleFalsePathChange = (nodeId: string) => {
    // console.log(`[DecisionRuleBuilder] False path changing from "${decisionData.falsePath}" to "${nodeId}"`);
    const updatedData = {
      ...decisionData,
      falsePath: nodeId || null
    };
    onChange(updatedData);
    // console.log("[DecisionRuleBuilder] Calling onUpdateEdges for false path change");
    onUpdateEdges?.(updatedData);
  };

  const getOperatorOptions = (type: "boolean" | "value") => {
    if (type === "boolean") {
      return [
        { value: "is_true", label: "is true" },
        { value: "is_false", label: "is false" }
      ];
    } else {
      return [
        { value: "==", label: "equals" },
        { value: "!=", label: "not equals" },
        { value: "contains", label: "contains" },
        { value: ">", label: "greater than" },
        { value: "<", label: "less than" },
        { value: ">=", label: "greater or equal" },
        { value: "<=", label: "less or equal" }
      ];
    }
  };

  return (
    <div className="max-h-96 overflow-y-auto">
      <div className="mb-4 flex items-center gap-2">
        <h3 className="text-sm font-gc-med text-foreground">Decision Logic</h3>
        <Tooltip content="Configure the logic for this decision node." side="right" immediate={true}>
          <InfoIcon className="w-4 h-4 text-maitai-green hover:text-maitai-green/80 transition-colors cursor-help" />
        </Tooltip>
      </div>

      <Card className="mb-4">
        <CardContent className="p-4">
          <div
            className="flex items-center cursor-pointer select-none font-medium text-base"
            onClick={() => setConditionsOpen(open => !open)}
          >
            Conditions
            <span className="ml-2 flex items-center">
              {conditionsOpen ? (
                <ChevronDownIcon className="w-3 h-3" />
              ) : (
                <ChevronRightIcon className="w-3 h-3" />
              )}
            </span>
          </div>
          <div 
            className="transition-all duration-300 ease-out overflow-hidden"
            style={{ 
              maxHeight: conditionsOpen ? "1000px" : "0px", 
              opacity: conditionsOpen ? 1 : 0, 
              transform: conditionsOpen ? "translateY(0)" : "translateY(-8px)" 
            }}
          >
            <div className="w-full pt-2 pb-2 space-y-2">
              {decisionData.conditions.map((condition, idx) => (
                <div key={condition.id || idx} className="flex items-center gap-2 flex-wrap mb-2 min-h-10">
                  {idx > 0 && (
                    <Select value={condition.joiner || "and"} onValueChange={value => handleJoinerChange(idx, value as "and" | "or")}>
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="and">AND</SelectItem>
                        <SelectItem value="or">OR</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  <Select value={condition.variable || undefined} onValueChange={value => handleConditionChange(idx, { variable: value })}>
                    <SelectTrigger className="flex-1 min-w-[100px]">
                      <SelectValue placeholder="variable" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(decisionVariablesRegistry).map((variable: string) => (
                        <SelectItem key={variable} value={variable}>{variable}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={condition.operator} onValueChange={value => handleConditionChange(idx, { operator: value as any })}>
                    <SelectTrigger className="w-25">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getOperatorOptions(condition.variable ? getVariableType(condition.variable) : condition.type).map(option => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(condition.variable ? getVariableType(condition.variable) : condition.type) === "value" && (
                    <Input
                      className="flex-1 min-w-[100px]"
                      placeholder="value"
                      value={condition.value}
                      onChange={e => handleConditionChange(idx, { value: e.target.value })}
                    />
                  )}
                  {idx > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveCondition(idx)}
                      className="ml-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2Icon className="w-4 h-4" />
                    </Button>
                  )}
              </div>
            ))}
            <Button
              variant="outline"
              onClick={handleAddCondition}
              className="w-full"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Add Condition
            </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Next Node Paths */}
      <div className="mb-4 flex items-center gap-2">
        <h3 className="text-sm font-gc-med text-foreground">Next Node Paths</h3>
        <Tooltip content="Configure where the conversation goes based on the decision outcome." side="right" immediate={true}>
          <InfoIcon className="w-4 h-4 text-maitai-green hover:text-maitai-green/80 transition-colors cursor-help" />
        </Tooltip>
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          {/* True Path */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground font-medium min-w-[80px]">True Path:</span>
            <Select value={decisionData.truePath || undefined} onValueChange={handleTruePathChange}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Select node" />
              </SelectTrigger>
              <SelectContent className="max-h-[200px] overflow-y-auto">
                {availableNodes
                  .filter(node => node.identifier && node.entityType === "BOT") // Only show BOT nodes with identifiers
                  .map(node => {
                    const entityType = node.entityType || "NODE";
                    const displayName = `${entityType}: ${node.identifier}`;
                    const tooltipTitle = node.content ? `${displayName} - ${node.content}` : displayName;
                    return (
                      <SelectItem key={node.id} value={node.identifier} title={tooltipTitle}>
                        <span className="block w-full overflow-hidden text-ellipsis whitespace-nowrap max-w-[300px]">
                          {displayName}
                        </span>
                      </SelectItem>
                    );
                  })}
              </SelectContent>
            </Select>
            {onCreateNodeForPath && (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const newNodeIdentifier = await onCreateNodeForPath('true');
                    // Automatically select the newly created node
                    handleTruePathChange(newNodeIdentifier);
                  } catch (error) {
                    toast.error("Failed to create node for true path");
                  }
                }}
                className="gap-1 text-maitai-green border-maitai-green hover:bg-maitai-green hover:text-white"
                title="Create new node for True path"
              >
                <PlusIcon className="w-4 h-4" />
                Create
              </Button>
            )}
          </div>

          {/* False Path */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground font-medium min-w-[80px]">False Path:</span>
            <Select value={decisionData.falsePath || undefined} onValueChange={handleFalsePathChange}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Select node" />
              </SelectTrigger>
              <SelectContent className="max-h-[200px] overflow-y-auto">
                {availableNodes
                  .filter(node => node.identifier && node.entityType === "BOT") // Only show BOT nodes with identifiers
                  .map(node => {
                    const entityType = node.entityType || "NODE";
                    const displayName = `${entityType}: ${node.identifier}`;
                    const tooltipTitle = node.content ? `${displayName} - ${node.content}` : displayName;
                    return (
                      <SelectItem key={node.id} value={node.identifier} title={tooltipTitle}>
                        <span className="block w-full overflow-hidden text-ellipsis whitespace-nowrap max-w-[300px]">
                          {displayName}
                        </span>
                      </SelectItem>
                    );
                  })}
              </SelectContent>
            </Select>
            {onCreateNodeForPath && (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const newNodeIdentifier = await onCreateNodeForPath('false');
                    // Automatically select the newly created node
                    handleFalsePathChange(newNodeIdentifier);
                  } catch (error) {
                    toast.error("Failed to create node for false path");
                  }
                }}
                className="gap-1 text-maitai-green border-maitai-green hover:bg-maitai-green hover:text-white"
                title="Create new node for False path"
              >
                <PlusIcon className="w-4 h-4" />
                Create
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DecisionRuleBuilder; 